import { Head, Link, router, useForm } from "@inertiajs/react";
import { format } from "date-fns";
import { ArrowLeft, FileSearch, XCircle } from "lucide-react";
import { useCallback } from "react";
import { CollapsibleSection } from "@/bizminer/Admin/InsightRequests/_components/CollapsibleSection.tsx";
import { ContextCard } from "@/bizminer/Admin/InsightRequests/_components/ContextCard.tsx";
import { ErrorMessageCard } from "@/bizminer/Admin/InsightRequests/_components/ErrorMessageCard.tsx";
import { StatusBadge } from "@/bizminer/Admin/InsightRequests/_components/StatusBadge.tsx";
import { StatusTimingCard } from "@/bizminer/Admin/InsightRequests/_components/StatusTimingCard.tsx";
import { TimestampsCard } from "@/bizminer/Admin/InsightRequests/_components/TimestampsCard.tsx";
import { WebhookEventsSection } from "@/bizminer/Admin/InsightRequests/_components/WebhookEventsSection.tsx";
import BizminerAdminLayout from "@/bizminer/layouts/admin/BizminerAdminLayout";
import { ConnectionStatus } from "@/components/ConnectionStatus";
import { Button } from "@/components/ui/button";
import { useAdminInsightRequestsChannel } from "@/hooks/useAdminInsightRequestsChannel";
import {
  bizminer_admin_insight_requests_path,
  cancel_bizminer_admin_insight_request_path,
} from "@/routes";
import type { BizminerAdminInsightRequestDetail } from "@/types/serializers";

interface Props {
  insight_request: BizminerAdminInsightRequestDetail;
}

const CancelButton = ({
  insightRequest,
}: {
  insightRequest: BizminerAdminInsightRequestDetail;
}) => {
  const { post, processing } = useForm({});

  const canCancel =
    insightRequest.in_progress && !!insightRequest.engine_request_id;

  const handleCancel = () => {
    if (!canCancel || processing) return;

    if (
      window.confirm(
        "Are you sure you want to cancel this request? Credits will be refunded.",
      )
    ) {
      post(cancel_bizminer_admin_insight_request_path(insightRequest.id));
    }
  };

  return (
    <Button
      variant="outline"
      disabled={!canCancel || processing}
      onClick={handleCancel}
      title={
        !insightRequest.engine_request_id
          ? "Request has not been submitted to engine"
          : !insightRequest.in_progress
            ? "Request is not in progress"
            : "Cancel this request"
      }
    >
      <XCircle className="h-4 w-4 mr-2" />
      {processing ? "Cancelling..." : "Cancel Request"}
    </Button>
  );
};

const PageHeader = ({
  insightRequest,
  isConnected,
}: {
  insightRequest: BizminerAdminInsightRequestDetail;
  isConnected: boolean;
}) => (
  <div className="bg-card border-b border-border">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex items-center gap-4 mb-4">
        <Link
          href={bizminer_admin_insight_requests_path()}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Insight Requests
        </Link>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <FileSearch className="h-6 w-6 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">
                Insight Request #{insightRequest.id}
              </h1>
              <StatusBadge status={insightRequest.status} />
            </div>
            <p className="text-muted-foreground mt-1">
              {insightRequest.pillar_insight.title} for{" "}
              {insightRequest.account.name}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <ConnectionStatus
            status={
              insightRequest.is_terminal
                ? "finalized"
                : isConnected
                  ? "connected"
                  : "disconnected"
            }
          />
          <CancelButton insightRequest={insightRequest} />
        </div>
      </div>
    </div>
  </div>
);

export default function Show({ insight_request }: Props) {
  const handleUpdate = useCallback(
    (updated: { id: number }) => {
      console.log("[Show] Received update:", updated);
      if (updated.id === insight_request.id) {
        console.log("[Show] Reloading for insight_request", insight_request.id);
        router.reload({ only: ["insight_request"] });
      }
    },
    [insight_request.id],
  );

  const { isConnected } = useAdminInsightRequestsChannel(handleUpdate, {
    enabled: !insight_request.is_terminal,
  });

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "—";
    return format(new Date(dateStr), "MMM d, yyyy HH:mm:ss");
  };

  return (
    <BizminerAdminLayout>
      <Head
        title={`Insight Request #${insight_request.id} - Super Admin - SalesMinr`}
      />
      <div className="min-h-screen bg-background">
        <PageHeader
          insightRequest={insight_request}
          isConnected={isConnected}
        />

        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ContextCard insightRequest={insight_request} />
            <StatusTimingCard insightRequest={insight_request} />
          </div>

          <TimestampsCard
            insightRequest={insight_request}
            formatDate={formatDate}
          />

          {insight_request.error_message && (
            <ErrorMessageCard message={insight_request.error_message} />
          )}

          <CollapsibleSection
            title="Request Data"
            description="The data sent to the analysis engine"
          >
            <pre className="text-xs bg-muted p-4 rounded-lg overflow-x-auto">
              {JSON.stringify(insight_request.request_data, null, 2)}
            </pre>
          </CollapsibleSection>

          <WebhookEventsSection events={insight_request.webhook_events} />
        </div>
      </div>
    </BizminerAdminLayout>
  );
}
